import React, { lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PlatformProvider, usePlatform } from "./context/PlatformContext";
import { useBamNoIndex } from "./lib/useSeo";
import Layout from "./components/Layout";
// v3.99c.63 — Home stays eagerly imported because it's the LCP page for
// logged-out visitors. Every other route is lazy-loaded via React.lazy
// to shrink the initial JS payload on mobile (PageSpeed Perf pass).
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
import BamPublicGate from "./components/BamPublicGate";
import ScrollToTop from "./components/ScrollToTop";
import UsernameChooserModal from "./components/UsernameChooserModal";
import PushEnableBanner from "./components/PushEnableBanner";

// Lazy public surfaces
const IntakeSheet         = lazy(() => import("./pages/IntakeSheet"));
const ForgotPassword      = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword       = lazy(() => import("./pages/ResetPassword"));
const EarlyAccess         = lazy(() => import("./pages/EarlyAccess"));
const BreederServices     = lazy(() => import("./pages/BreederServices"));
const Pricing             = lazy(() => import("./pages/Pricing"));
const Community           = lazy(() => import("./pages/Community"));
const Browse              = lazy(() => import("./pages/Browse"));
const VaultMarket         = lazy(() => import("./pages/VaultMarket"));
const ForBreeders         = lazy(() => import("./pages/ForBreeders"));
const Families            = lazy(() => import("./pages/Families"));
const TraitLandingPage    = lazy(() => import("./pages/TraitLandingPage"));
const Shop                = lazy(() => import("./pages/Shop"));
const ArticleSystem       = lazy(() => import("./components/articles/ArticleSystem"));
const ListingDetail       = lazy(() => import("./pages/ListingDetail"));
const BreederShopCatalog  = lazy(() => import("./pages/BreederShopCatalog"));
const Breeders            = lazy(() => import("./pages/Breeders"));
const BreederStorefront   = lazy(() => import("./pages/BreederStorefront"));
const ForgeFeed           = lazy(() => import("./pages/ForgeFeed"));
const Cart                = lazy(() => import("./pages/Cart"));
const Checkout            = lazy(() => import("./pages/Checkout"));
const CheckoutSuccess     = lazy(() => import("./pages/CheckoutSuccess"));
const CheckoutCancel      = lazy(() => import("./pages/CheckoutCancel"));
const CustomerOrderDetail = lazy(() => import("./pages/CustomerOrderDetail"));
const Login               = lazy(() => import("./pages/Login"));
const Register            = lazy(() => import("./pages/Register"));
const BonusExplainer      = lazy(() => import("./pages/BonusExplainer"));
const BuildTheVaultPage   = lazy(() => import("./pages/BuildTheVault"));
const AuthCallback        = lazy(() => import("./pages/AuthCallback"));
const CustomerDashboard   = lazy(() => import("./pages/CustomerDashboard"));
const ForgeFunds          = lazy(() => import("./pages/ForgeFunds"));
const VendorDashboard     = lazy(() => import("./pages/VendorDashboard"));
const AdminDashboard      = lazy(() => import("./pages/AdminDashboard"));
const StaticPage          = lazy(() => import("./pages/Static"));
const Stub                = lazy(() => import("./pages/Stub"));
const Trending            = lazy(() => import("./pages/Trending"));
const ApplyBreeder        = lazy(() => import("./pages/ApplyBreeder"));
const JoinCollector       = lazy(() => import("./pages/JoinCollector"));
const Dispatch            = lazy(() => import("./pages/Dispatch"));
const DispatchArticle     = lazy(() => import("./pages/DispatchArticle"));
const MyBids              = lazy(() => import("./pages/MyBids"));
const AuctionPay          = lazy(() => import("./pages/AuctionPay"));
const OnboardVendor       = lazy(() => import("./pages/OnboardVendor"));
const Giveaways           = lazy(() => import("./pages/Giveaways"));
const GiveawayDetail      = lazy(() => import("./pages/GiveawayDetail"));
const AdminImport         = lazy(() => import("./pages/AdminImport"));
const AdminInvoices       = lazy(() => import("./pages/AdminInvoices"));
const PublicInvoice       = lazy(() => import("./pages/PublicInvoice"));
const VendorImport        = lazy(() => import("./pages/VendorImport"));
const LineageAtlas        = lazy(() => import("./pages/LineageAtlas"));
const LineageDetail       = lazy(() => import("./pages/LineageDetail"));
const GeneticsShareRedirect = lazy(() => import("./pages/GeneticsShareRedirect"));
const Discover            = lazy(() => import("./pages/Discover"));
const DropsCalendar       = lazy(() => import("./pages/DropsCalendar"));
const DropDetail          = lazy(() => import("./pages/DropDetail"));
const AboutCatalog        = lazy(() => import("./pages/AboutCatalog"));
const GrowLogs            = lazy(() => import("./pages/GrowLogs"));
const GrowLogNew          = lazy(() => import("./pages/GrowLogNew"));
const GrowLogDetail       = lazy(() => import("./pages/GrowLogDetail"));
// v3.99d.275 — Verified Grow-Offs
const GrowOffs            = lazy(() => import("./pages/GrowOffs"));
const GrowOffDetail       = lazy(() => import("./pages/GrowOffDetail"));
const GrowOffSubmit       = lazy(() => import("./pages/GrowOffSubmit"));
const GrowOffCreate       = lazy(() => import("./pages/GrowOffCreate"));
const Vault               = lazy(() => import("./pages/Vault"));
const VaultCertificate    = lazy(() => import("./pages/VaultCertificate"));
const LandingPage         = lazy(() => import("./pages/LandingPage"));
const GiveawayAuditLedger = lazy(() => import("./pages/GiveawayAuditLedger"));
const Trade               = lazy(() => import("./pages/Trade"));
const PremiumTiers        = lazy(() => import("./pages/PremiumTiers"));
const CollectorProfile    = lazy(() => import("./pages/CollectorProfile"));
const FieldAccess         = lazy(() => import("./pages/FieldAccess"));

// Pricing / ArticleSystem expose multiple named exports — wrap them lazily.
const PricingOverview     = lazy(() => import("./pages/Pricing").then((m) => ({ default: m.PricingOverview })));
const CollectorPlusPage   = lazy(() => import("./pages/Pricing").then((m) => ({ default: m.CollectorPlusPage })));
const BreederPricingPage  = lazy(() => import("./pages/Pricing").then((m) => ({ default: m.BreederPricingPage })));
// v3.99c.90 — live, payments-driven breeder-tier pricing page.
const BreederTiersPricingPage = lazy(() => import("./pages/BreederTiersPricingPage"));
const ArticleIndexCmp     = lazy(() => import("./components/articles/ArticleSystem").then((m) => ({ default: m.ArticleIndex })));
const ArticleDetailCmp    = lazy(() => import("./components/articles/ArticleSystem").then((m) => ({ default: m.ArticleDetail })));

// v3.99c.63 — Route-level Suspense fallback. Lightweight skeleton so users
// never see a flash of white while a code-split chunk loads. Matches the
// existing initial-loader from index.html visually.
function RouteFallback() {
  return (
    <div role="status" aria-label="Loading"
         style={{
           minHeight: "60vh",
           display: "flex",
           alignItems: "center",
           justifyContent: "center",
           background: "var(--bg, #08090B)",
         }}>
      <span style={{
        width: 24, height: 24,
        border: "2px solid rgba(79,168,111,0.2)",
        borderTopColor: "#4FA86F",
        borderRadius: "50%",
        animation: "spin 0.9s linear infinite",
      }} />
    </div>
  );
}

// Logged-out visitors see the cinematic pre-launch landing at "/".
// Logged-in users land directly on the marketplace home.
// v3.78 — Under Bank Approval Mode, anonymous visitors landing on "/" are
// routed to /about/catalog instead of the breeder/founding-breeder recruitment
// landing so the public homepage carries zero breeder-services / vendor
// onboarding language during processor review.
// v3.99c.60 — Logged-out visitors now land on the rebuilt presentation Home
// page (the full 11-section CannaForge platform showcase). Previously they
// were routed to `<EarlyAccess />` which made sense during waitlist + BAM
// era, but no longer reflects the platform state. EarlyAccess is still
// reachable via /early-access for anyone who wants the founding-breeder
// recruitment surface.
//
// BAM behaviour preserved: when BAM mode is active, visitors are still
// redirected to /about/catalog (bank-safe curated copy) — that gate is
// untouched.
function RootGate() {
  const { user } = useAuth();
  const { bank_approval_mode, loaded, eval_mode } = usePlatform();
  if (user === null) return null; // still resolving auth — avoid flicker
  if (user && user.role) return <Home />;
  // v3.99ba — Field Access evaluators (anonymous) land directly on the
  // full collector Home so AI agents / reviewers see the actual product
  // surface, not the founding-breeder recruitment landing.
  if (eval_mode) return <Home />;
  if (loaded && bank_approval_mode) return <Navigate to="/about/catalog" replace />;
  // v3.99c.60 — Default logged-out visitor now sees the rebuilt Home.
  return <Home />;
}

// Compliance toggle gate: when public auctions are disabled, any direct
// navigation to an auction-specific route (`/auctions`, `/my/bids`) is
// silently redirected to the neutral browse page. Keeps the routes in the
// codebase so re-enabling is a single toggle flip.
// v3.76 — also emits `noindex,nofollow` while Bank Approval Mode is ON so
// auction URLs don't get indexed during processor underwriting.
function AuctionGate({ children, redirectTo = "/browse" }) {
  const { public_auctions_enabled, bank_approval_mode, loaded } = usePlatform();
  useBamNoIndex(bank_approval_mode);
  if (!loaded) return null; // wait for settings — avoid auction flash
  if (!public_auctions_enabled) return <Navigate to={redirectTo} replace />;
  return children;
}

// v3.99by — Redirects legacy /learn/:slug → /genetics-guide/:slug (301 via React).
function LegacyLearnRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/genetics-guide/${slug}`} replace />;
}

// v3.99c.4 — Redirects legacy /grow-advice/:slug → /grower-resources/:slug.
function LegacyGrowAdviceRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/grower-resources/${slug}`} replace />;
}

function App() {
  return (
    /*
     * v3.99bu — Canonical SEO entity routes (always-public, never BAM-gated):
     *   /strains/:slug      — Product + lineage entity (and /lineage/:slug alias)
     *   /breeder/:id        — Organization + Brand + OfferCatalog entity
     *   /families           — Genetic family taxonomy index
     *   /families/:slug     — DefinedTerm + CollectionPage family hub
     *   /learn              — Knowledge base index
     *   /learn/:slug        — Article + FAQPage learn topic
     *   /lineage            — Lineage Atlas (registry view)
     *
     * These ship `index, follow` headers and full JSON-LD on every render,
     * regardless of Bank Approval Mode. They form the long-term SEO moat:
     * transactional surfaces (cart, checkout, auctions) stay gated to BAM,
     * but the entity graph is always crawler-legible.
     */
    <AuthProvider>
      <PlatformProvider>
        <BrowserRouter>
        <ScrollToTop />
        <UsernameChooserModal />
        <Layout>
          <PushEnableBanner />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<RootGate />} />
            {/* v3.99ba — Private public-evaluation entry point.
                Visiting `/field-access/{slug}` with the slug that matches
                the backend env var `PUBLIC_EVALUATOR_ACCESS_SLUG` activates
                a per-tab override that suppresses BAM, preview banner,
                and the age modal so external evaluators see the fully
                launched product. Slug is stripped from the URL on entry. */}
            <Route path="/field-access/:slug" element={<FieldAccess />} />
            <Route path="/early-access" element={<Navigate to="/register" replace />} />
            <Route path="/breeder-services" element={<BamPublicGate><BreederServices /></BamPublicGate>} />
            {/* v3.99c — Pricing surfaces. /pricing is the cross-audience
                overview; /collector-plus and /breeder-pricing are deep
                pages. All three respect rollout flags so a tier flagged
                "coming_soon" renders with a soft CTA instead of checkout. */}
            <Route path="/pricing" element={<PricingOverview />} />
            <Route path="/pricing/breeder-tiers" element={<BreederTiersPricingPage />} />
            {/* v3.99d.286 — Canonical breeder-tiers route is
                /pricing/breeder-tiers. Bare /breeder-tiers is a duplicate
                redirect for any legacy inbound link. /breeder-pricing
                points to a DIFFERENT page (BreederPricingPage) and is
                intentionally not collapsed in this phase. */}
            <Route path="/breeder-tiers" element={<Navigate to="/pricing/breeder-tiers" replace />} />
            <Route path="/collector-plus" element={<CollectorPlusPage />} />
            <Route path="/breeder-pricing" element={<BreederPricingPage />} />
            {/* v3.99ag — Breeder application is ALWAYS reachable, even
                during Bank Approval Mode. CannaForge is actively recruiting
                breeders; dead-ending Apply → /about/catalog blocked the
                onboarding funnel. We still emit noindex via BamPublicGate
                (without `redirectWhenBam`) so the page won't be crawled,
                but breeders who arrive via direct link, footer, or any of
                the in-app CTAs land on the real form. */}
            <Route path="/join/breeder" element={<BamPublicGate><ApplyBreeder /></BamPublicGate>} />
            <Route path="/apply/breeder" element={<Navigate to="/join/breeder" replace />} />
            <Route path="/join/collector" element={<BamPublicGate><JoinCollector /></BamPublicGate>} />
            <Route path="/marketplace" element={<Home />} />
            <Route path="/browse" element={<Browse />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/auctions" element={<AuctionGate><Browse defaultType="auction" /></AuctionGate>} />
            {/* v3.99c.20 — Phase 8: Programmatic SEO landing pages. Single
                config-driven component pulling /api/landing/<slug>. Routes
                automatically opt-in to noindex when content density is low,
                auto-promote to indexable when breeders/admins seed more data.

                v3.99c.40 — Junk legacy routes (/seeds/* generic stubs and
                /genetics/*-family) now 301-redirect to the canonical trait
                landing pages (/seeds/:slug and /families/:slug). Family and
                seed pages share the same unified TraitLandingPage component
                via the type-specific routes added below. */}
            <Route path="/seeds/landrace"     element={<Navigate to="/preservation/landrace" replace />} />
            <Route path="/seeds/:slug"        element={<TraitLandingPage traitType="seeds" />} />
            <Route path="/genetics/kush-family"     element={<Navigate to="/families/kush" replace />} />
            <Route path="/genetics/haze-family"     element={<Navigate to="/families/haze" replace />} />
            <Route path="/genetics/skunk-family"    element={<Navigate to="/families/skunk" replace />} />
            <Route path="/genetics/og-family"       element={<Navigate to="/families/og" replace />} />
            <Route path="/genetics/cookies-family"  element={<Navigate to="/families/cookies" replace />} />
            <Route path="/genetics/diesel-family"   element={<Navigate to="/families/diesel" replace />} />
            <Route path="/genetics/runtz-family"    element={<Navigate to="/families/runtz" replace />} />
            <Route path="/genetics/purple-family"   element={<Navigate to="/families/purple" replace />} />
            <Route path="/genetics/cake-family"     element={<Navigate to="/families/cake" replace />} />
            <Route path="/vault-releases" element={<Navigate to="/the-vault" replace />} />
            <Route path="/buy-now" element={<Browse defaultType="buy_now" />} />
            <Route path="/the-vault" element={<VaultMarket />} />
            <Route path="/for-breeders" element={<ForBreeders />} />
            <Route path="/families" element={<Families />} />
            {/* v3.99c.40 — Unified TraitLandingPage for all 7 trait/entity
                page types (families/terpenes/aromas/classifications/seeds/
                preservation/structures). Single data shape from
                /api/traits/{type}/{slug} backs every page. Commercial-first
                layout — listings before lineage strains. Educational copy
                LLM-generated and cached. SEO/JSON-LD per page. */}
            <Route path="/families/:slug"        element={<TraitLandingPage traitType="families" />} />
            <Route path="/terpenes/:slug"        element={<TraitLandingPage traitType="terpenes" />} />
            <Route path="/aromas/:slug"          element={<TraitLandingPage traitType="aromas" />} />
            <Route path="/classifications/:slug" element={<TraitLandingPage traitType="classifications" />} />
            <Route path="/preservation/:slug"    element={<TraitLandingPage traitType="preservation" />} />
            <Route path="/structures/:slug"      element={<TraitLandingPage traitType="structures" />} />
            {/* v3.99by — Genetics Guide + Grow Advice (CMS-backed). Legacy
                /learn/:slug routes redirect to /genetics-guide/:slug to
                preserve SEO and bookmarks. v3.99c.4 renamed
                /grow-advice → /grower-resources; old URLs redirect. */}
            <Route path="/learn" element={<Navigate to="/genetics-guide" replace />} />
            <Route path="/learn/:slug" element={<LegacyLearnRedirect />} />
            <Route path="/genetics-guide" element={<ArticleIndexCmp type="genetics_guide" />} />
            <Route path="/genetics-guide/:slug" element={<ArticleDetailCmp type="genetics_guide" />} />
            <Route path="/grow-advice" element={<Navigate to="/grower-resources" replace />} />
            <Route path="/grow-advice/:slug" element={<LegacyGrowAdviceRedirect />} />
            <Route path="/grower-resources" element={<ArticleIndexCmp type="grow_advice" />} />
            <Route path="/grower-resources/:slug" element={<ArticleDetailCmp type="grow_advice" />} />
            {/* v3.99d.200 — Long-term SEO URL system. The clean
                slug-based product URL is the canonical public route;
                the old UUID route resolves the listing then redirects
                to the canonical URL (the seo_render.py prerender path
                issues a real HTTP 301 for crawler traffic, this
                client-side redirect catches direct browser hits). */}
            <Route path="/shop/:breederSlug/:productSlug" element={<ListingDetail />} />
            <Route path="/shop/:breederSlug" element={<BreederShopCatalog />} />
            <Route path="/listing/:id" element={<ListingDetail />} />
            <Route path="/breeders" element={<Breeders />} />
            <Route path="/breeder/:id" element={<BreederStorefront />} />
            <Route path="/forge-feed" element={<ForgeFeed />} />
            <Route path="/lineage" element={<LineageAtlas />} />
            <Route path="/lineage/:id_or_slug" element={<LineageDetail />} />
            {/* v3.99c.52 — Short share URLs for the Genetics Map. Canonical
                link points to /strains/:slug so SEO juice flows to the
                strain page; humans get redirected to the map. */}
            <Route path="/g/:slug" element={<GeneticsShareRedirect />} />
            {/* v3.99br — Canonical strain entity routes. Each strain
                exposes its lineage graph + relationships + JSON-LD as a
                permanent SEO node. Aliased to LineageDetail (lineage
                records ARE strain records) with the canonical URL
                preferring /strains/:slug. */}
            <Route path="/strains/:id_or_slug" element={<LineageDetail />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/discover/:classification" element={<Discover />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/checkout/cancel" element={<CheckoutCancel />} />
            <Route path="/account/orders/:order_id" element={<CustomerOrderDetail />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/bonus" element={<BonusExplainer />} />
            <Route path="/build-the-vault" element={<BuildTheVaultPage />} />
            {/* v3.99d.341o — Emergent-managed Google OAuth callback.
                Emergent redirects the browser to
                /auth/google/callback#session_id=<id>. The AuthCallback
                page exchanges it via POST /api/auth/google/session
                and navigates the user to their role-appropriate
                landing page. */}
            <Route path="/auth/google/callback" element={<AuthCallback />} />
            <Route path="/account" element={<CustomerDashboard />} />
            <Route path="/account/forge-funds" element={<ForgeFunds />} />
            <Route path="/my/bids" element={<AuctionGate redirectTo="/account"><MyBids /></AuctionGate>} />
            <Route path="/auctions/:listingId/pay" element={<AuctionGate redirectTo="/account"><AuctionPay /></AuctionGate>} />
            <Route path="/giveaways" element={<Giveaways />} />
            {/* v3.99c.22 — Public cryptographically-auditable draw ledger.
                MUST be registered BEFORE the :slug route or React Router
                will try to load a giveaway with slug "audit". */}
            <Route path="/giveaways/audit" element={<GiveawayAuditLedger />} />
            <Route path="/giveaways/:slug" element={<GiveawayDetail />} />
            <Route path="/intake-sheet/:id" element={<IntakeSheet />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/onboard/vendor" element={<BamPublicGate><OnboardVendor /></BamPublicGate>} />
            <Route path="/vendor" element={<VendorDashboard />} />
            <Route path="/vendor/dashboard" element={<VendorDashboard />} />
            <Route path="/vendor/import" element={<VendorImport />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/import" element={<AdminImport />} />
            <Route path="/admin/invoices" element={<AdminInvoices />} />
            <Route path="/invoice/:token" element={<PublicInvoice />} />
            <Route path="/terms" element={<StaticPage page="terms" />} />
            <Route path="/privacy" element={<StaticPage page="privacy" />} />
            <Route path="/compliance" element={<StaticPage page="compliance" />} />
            <Route path="/shipping" element={<StaticPage page="shipping" />} />
            <Route path="/faq" element={<StaticPage page="faq" />} />
            <Route path="/contact" element={<StaticPage page="contact" />} />
            <Route path="/grow-guides" element={<Stub kind="grow-guides" />} />
            <Route path="/grow-logs" element={<GrowLogs />} />
            <Route path="/grow-logs/new" element={<GrowLogNew />} />
            <Route path="/grow-logs/:slug" element={<GrowLogDetail />} />
            {/* v3.99d.275 — Verified Grow-Offs */}
            <Route path="/grow-offs" element={<GrowOffs />} />
            <Route path="/grow-offs/new" element={<GrowOffCreate />} />
            <Route path="/grow-offs/:slug" element={<GrowOffDetail />} />
            <Route path="/grow-offs/:slug/submit" element={<GrowOffSubmit />} />
            <Route path="/vault" element={<BamPublicGate redirectWhenBam><Vault /></BamPublicGate>} />
            <Route path="/vault/:slug" element={<BamPublicGate redirectWhenBam><VaultCertificate /></BamPublicGate>} />
            <Route path="/trade" element={<Trade />} />
            <Route path="/premium" element={<PremiumTiers />} />
            <Route path="/collectors/:id" element={<CollectorProfile />} />
            <Route path="/about/catalog" element={<AboutCatalog />} />
            <Route path="/new-drops" element={<DropsCalendar />} />
            <Route path="/drops" element={<Navigate to="/new-drops" replace />} />
            <Route path="/drop/:slug_or_id" element={<DropDetail />} />
            <Route path="/guild" element={<Navigate to="/community" replace />} />
            <Route path="/community" element={<Community />} />
            <Route path="/dispatch" element={<Dispatch />} />
            <Route path="/dispatch/:slug" element={<DispatchArticle />} />
            <Route path="/trending" element={<Trending />} />
            <Route path="/about" element={<Stub kind="about" />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </Layout>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
      </PlatformProvider>
    </AuthProvider>
  );
}

export default App;
